import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { adminApi } from '@/api/adminApi'
import type { AdminUserDetail } from '@/api/types'
import { AdminCard, AdminPageHeader } from '@/components/admin/AdminUi'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { Skeleton } from '@/components/ui/Skeleton'
import { morphToast } from '@/components/ui/Toast'

export default function UserDetailPage() {
  const navigate = useNavigate()
  const userId = Number(useParams().userId)
  const [user, setUser] = useState<AdminUserDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [sending, setSending] = useState<'reset' | 'verification' | null>(null)

  useEffect(() => { adminApi.user(userId).then(setUser).catch((err: Error) => setError(err.message)) }, [userId])
  if (error) return <EmptyState title="Erro ao carregar usuário" description={error} />
  if (!user) return <Skeleton height={320} />

  const send = async (kind: 'reset' | 'verification') => {
    const label = kind === 'reset' ? 'recuperação de senha' : 'verificação de e-mail'
    if (!window.confirm(`Enviar e-mail de ${label} para ${user.email}?`)) return
    setSending(kind)
    try {
      if (kind === 'reset') await adminApi.sendPasswordReset(user.id)
      else await adminApi.resendUserVerification(user.id)
      morphToast.success(`E-mail de ${label} solicitado.`)
    } catch (err) {
      morphToast.fromError(err)
    } finally {
      setSending(null)
    }
  }

  return (
    <div>
      <AdminPageHeader
        title={user.name}
        description={user.email}
        actions={<div className="flex flex-wrap gap-2"><Button disabled={!user.is_active || sending !== null} variant="primary" onClick={() => void send('reset')}>{sending === 'reset' ? 'Enviando…' : 'Enviar recuperação de senha'}</Button>{!user.email_verified ? <Button disabled={!user.is_active || sending !== null} onClick={() => void send('verification')}>{sending === 'verification' ? 'Enviando…' : 'Reenviar verificação'}</Button> : null}</div>}
      />
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <AdminCard><div className="text-xs" style={{ color: 'var(--text-muted)' }}>Conta</div><strong className="mt-2 block">{user.is_active ? 'Ativa' : 'Desativada'}</strong></AdminCard>
        <AdminCard><div className="text-xs" style={{ color: 'var(--text-muted)' }}>E-mail</div><strong className="mt-2 block">{user.email_verified ? 'Verificado' : 'Não verificado'}</strong></AdminCard>
        <AdminCard><div className="text-xs" style={{ color: 'var(--text-muted)' }}>Último acesso</div><strong className="mt-2 block">{user.last_login ? new Date(user.last_login).toLocaleString('pt-BR') : 'Nunca'}</strong></AdminCard>
      </div>
      <AdminCard>
        <h2 className="mb-3 text-sm font-semibold">Workspaces ({user.workspaces.length})</h2>
        <p className="mb-3 text-xs" style={{ color: 'var(--text-muted)' }}>Dê dois cliques em uma linha para gerenciar o acesso do workspace.</p>
        {user.workspaces.length === 0 ? <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Este usuário não possui workspaces.</p> : user.workspaces.map((workspace) => (
          <div key={`${workspace.id}-${workspace.role}`} tabIndex={workspace.is_active ? 0 : undefined} aria-label={`Abrir workspace ${workspace.name}`} onDoubleClick={() => { if (workspace.is_active) navigate(`/admin/accounts/${workspace.id}`) }} onKeyDown={(event) => { if (workspace.is_active && event.target === event.currentTarget && event.key === 'Enter') navigate(`/admin/accounts/${workspace.id}`) }} className={`flex flex-wrap items-center justify-between gap-3 border-t rounded-lg px-3 py-4 text-xs ${workspace.is_active ? 'cursor-pointer transition-colors hover:bg-[var(--bg-toolbar)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]' : ''}`} style={{ borderColor: 'var(--border)' }}>
            <div>{workspace.is_active ? <Link className="font-semibold" style={{ color: 'var(--accent)' }} to={`/admin/accounts/${workspace.id}`}>{workspace.name}</Link> : <strong>{workspace.name}</strong>}<div style={{ color: 'var(--text-dim)' }}>/{workspace.slug} · papel: {workspace.role}</div></div>
            <div className="text-right"><strong>{workspace.is_active ? 'Ativo' : 'Desativado'}</strong><div style={{ color: 'var(--text-dim)' }}>{workspace.plan_code ?? 'sem plano'} · {workspace.subscription_status ?? 'sem assinatura'}{!workspace.membership_active ? ' · vínculo inativo' : ''}</div></div>
          </div>
        ))}
      </AdminCard>
    </div>
  )
}
